import React, { useState, useEffect } from 'react';
import { api } from '../config/api';
import { Stethoscope, Save, ShieldCheck, Loader2, User, CreditCard, Plus, ArrowLeft, Edit, Trash2, Search, History, Trash, MapPin } from 'lucide-react';
import toast from '../utils/toast';
import { formatDisplayDate, normalizeDateInput, toDateInputValue } from '../utils/dateFormat';
import { isValidEmail, isValidPhoneNumber, normalizeEmailInput, normalizePhoneInput } from '../utils/formValidation';
import SearchBar from '../components/SearchBar';
import {
  masterAccentButton,
  masterHeader,
  masterHeaderCard,
  masterHeaderIcon,
  masterHeaderInfo,
  masterInput,
  masterInputLabel,
  masterPageShell,
  masterPanel,
  masterPrimaryButton,
  masterSearchInput,
  masterSearchWrap,
  masterStickyHeadRow,
  masterToolbar,
  masterSubtitle,
  masterTitle,
  masterTableID,
  masterTableData,
  
} from '../components/masterPageStyles';

const createFeeHistoryRow = () => ({
  rowKey: `fee-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  consultationFee: '',
  revenueShareType: 'Percentage',
  doctorShare: '',
  centerShare: '',
  fromDate: '',
  toDate: ''
});

const normalizeFeeHistory = (feeHistory = []) => {
  const normalized = feeHistory.map((row, index) => ({
    ...row,
    rowKey: row.rowKey || row._id || row.id || `fee-existing-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 7)}`
  }));

  return normalized.length ? normalized : [createFeeHistoryRow()];
};

const getInitialFormData = () => ({
  doctorId: '',prefix: 'DR', doctorName: '', dob: '',gender: '', qualification: '', specialization: '', registrationNumber: '', mobile: '', email: '',
  vill: '', po: '', ps: '', dist: '', pin: '', stateName: '',
  department: '', status: 'Active', joiningDate: toDateInputValue(new Date()), terminationDate: '',
  feeHistory: [createFeeHistoryRow()]
});

const DoctorRegistration = () => {
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [showForm, setShowForm] = useState(true); // Default to form page view
  const [editingDoctorId, setEditingDoctorId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [formData, setFormData] = useState(getInitialFormData());

  const fetchDoctors = async () => {
    try {
      const res = await api.get(`/doctors/all`);
      if (res.data.success) setDoctors(res.data.data || []);
    } catch (err) { toast.error('FAILED TO FETCH DOCTOR LIST'); }
  };

  const fetchInitialData = async () => {
    try {
      const [deptRes, idRes] = await Promise.all([
        api.get(`/master/department/all`),
        api.get(`/doctors/latest-id`)
      ]);
      if (deptRes.data.success) setDepartments(deptRes.data.data);
      if (idRes.data.success) {
        // Only update ID if we are NOT editing an existing doctor
        setFormData(prev => editingDoctorId ? prev : { ...prev, doctorId: idRes.data.nextId });
      }
    } catch (err) { toast.error('SYNC ERROR'); }
  };

  useEffect(() => { fetchDoctors(); fetchInitialData(); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let val = value;
    if (name === 'email') val = normalizeEmailInput(value);
    if (name === 'mobile') val = normalizePhoneInput(value);
    if (name === 'pin') val = String(value || '').replace(/\D/g, '').slice(0, 6);
    if (['joiningDate', 'dob', 'terminationDate'].includes(name)) val = normalizeDateInput(value);
    if (!['email', 'mobile', 'pin', 'joiningDate', 'dob', 'terminationDate','status'].includes(name)) {
      val = String(val || '').toUpperCase();
    }
    setFormData(prev => ({ ...prev, [name]: val }));
  };

  const openAddForm = async () => {
    setEditingDoctorId(null);
    const idRes = await api.get(`/doctors/latest-id`);
    const nextId = idRes.data?.success ? idRes.data.nextId : '';
    setFormData({ ...getInitialFormData(), doctorId: nextId });
    setShowForm(true);
  };

const handleFeeChange = (id, field, value) => {
    setFormData(prev => {
      const updatedHistory = prev.feeHistory.map(row => {
        if (row.rowKey === id) {
          let newRow = { ...row, [field]: value };
          if (['fromDate', 'toDate'].includes(field)) newRow[field] = normalizeDateInput(value);

          // --- DATE VALIDATION LOGIC ---
          if (newRow.fromDate && newRow.toDate && newRow.fromDate.length === 10 && newRow.toDate.length === 10) {
            const [fD, fM, fY] = newRow.fromDate.split('/').map(Number);
            const [tD, tM, tY] = newRow.toDate.split('/').map(Number);
            const startDate = new Date(fY, fM - 1, fD);
            const endDate = new Date(tY, tM - 1, tD);

            if (endDate < startDate) {
              toast.error("TO DATE CANNOT BE BEFORE FROM DATE");
              newRow.toDate = ''; // Reset invalid date
            }
          }

          // --- REVENUE CALCULATION ---
          const fee = parseFloat(newRow.consultationFee) || 0;
          const dShare = parseFloat(newRow.doctorShare) || 0;
          const cShare = parseFloat(newRow.centerShare) || 0;

          if (newRow.revenueShareType === 'Percentage') {
            if (field === 'doctorShare') newRow.centerShare = dShare <= 100 ? (100 - dShare).toString() : '0';
          } else {
            if (field === 'doctorShare') newRow.centerShare = (fee - dShare).toString();
            else if (field === 'centerShare') newRow.doctorShare = (fee - cShare).toString();
            else if (field === 'consultationFee') newRow.centerShare = (fee - dShare).toString();
          }
          return newRow;
        }
        return row;
      });
      return { ...prev, feeHistory: updatedHistory };
    });
  };


  const handleDelete = async (doctor) => {
    const confirmed = window.confirm(`DELETE ${doctor.doctorName} (${doctor.doctorId})?`);
    if (!confirmed) return;
    try {
      const res = await api.delete(`/doctors/${doctor._id}`);
      if (res.data.success) {
        toast.success('DOCTOR DELETED SUCCESSFULLY!');
        await fetchDoctors();
        // If we were editing this doctor, close the form
        if (editingDoctorId === doctor._id) {
          setShowForm(false);
          setEditingDoctorId(null);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'DELETE FAILED');
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValidPhoneNumber(formData.mobile)) return toast.error('Mobile number must be exactly 10 digits');
    if (formData.email && !isValidEmail(formData.email)) return toast.error('Enter a valid email address');
    setLoading(true);
    try {
      const payload = {
        ...formData,
        doctorName: formData.doctorName.trim().toUpperCase(),
        gender: String(formData.gender || '').trim().toUpperCase(),
        qualification: formData.qualification.trim().toUpperCase(),
        registrationNumber: formData.registrationNumber.trim().toUpperCase(),
        vill: String(formData.vill || '').trim().toUpperCase(),
        po: String(formData.po || '').trim().toUpperCase(),
        ps: String(formData.ps || '').trim().toUpperCase(),
        dist: String(formData.dist || '').trim().toUpperCase(),
        stateName: String(formData.stateName || '').trim().toUpperCase(),
        department: String(formData.department || '').trim().toUpperCase(),
        email: normalizeEmailInput(formData.email),
        mobile: normalizePhoneInput(formData.mobile),
        feeHistory: formData.feeHistory.map(({ rowKey, ...row }) => row)
      };
    const res = editingDoctorId ? await api.put(`/doctors/update/${editingDoctorId}`, payload) : await api.post(`/doctors/register`, payload);
      if (res.data.success) {
        toast.success(editingDoctorId ? 'UPDATED!' : 'REGISTERED!');
        setFormData(getInitialFormData()); // Clear fields for a fresh entry
        setEditingDoctorId(null);
        fetchDoctors();
        setShowForm(true); // Explicitly stay on the form page view
      }
    } catch (err) { toast.error(err.response?.data?.message || 'SAVE ERROR'); } finally { setLoading(false); }
  };

  const filteredDoctors = React.useMemo(() => {
    const sortedList = [...doctors].sort((a, b) => {
      const idA = String(a.doctorId || a._id || '');
      const idB = String(b.doctorId || b._id || '');
      return idB.localeCompare(idA, undefined, { numeric: true, sensitivity: 'base' });
    });

    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedList;

    return sortedList.filter((d) => {
      const name = (d.doctorName || '').toLowerCase();
      const id = (d.doctorId || '').toLowerCase();
      return name.startsWith(q) || id.startsWith(q);
    });
  }, [doctors, searchTerm]);

  const paginatedDoctors = React.useMemo(() => {
    if (pageSize === -1) return filteredDoctors;
    const start = (currentPage - 1) * pageSize;
    return filteredDoctors.slice(start, start + pageSize);
  }, [filteredDoctors, currentPage, pageSize]);

  return (
       <div className={masterPageShell}>
         <header className={masterHeader}>
           <div className={masterHeaderCard}>
               <div className={masterHeaderInfo}>
            <div className={masterHeaderIcon}><Stethoscope size={10}/>
            </div>
            <div><h1 className={masterTitle}>Doctor Registration</h1>
            {/* <p className={masterSubtitle}>Asha Hope Medical</p> */}
            </div>
          </div>
      <button onClick={() => setShowForm(!showForm)} className={showForm ? masterPrimaryButton : masterAccentButton}>
             {showForm ? <ArrowLeft size={14} /> : <Plus size={14} />}{showForm ? 'VIEW REGISTERED LIST' : 'OPEN REGISTRATION FORM'}
          </button>
        </div>
      </header>

      <main className="px-2">
       
      {!showForm ? (
        <div className="max-w-7xl mx-auto bg-white rounded-xl border border-slate-400 overflow-hidden shadow-sm">
          <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search Doctor..."
              totalItems={filteredDoctors.length}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />

          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left">
              <thead>
                <tr className={masterStickyHeadRow}>
                  <th className={masterTableData}>ID</th>
                  <th className={masterTableData}>Doctor Name</th>
                  <th className={masterTableData}>Department</th>
                  <th className={masterTableData}>Fee</th>
                  <th className={masterTableData}>Status</th>
                  <th className={masterTableData}>Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-300">
                {paginatedDoctors.length > 0 ? (
                  paginatedDoctors.map(d => (
                    <tr key={d._id} className="hover:bg-emerald-50/30 transition-colors">
                      <td className={masterTableID}>
                        {d.doctorId}
                      </td>
                      <td className={masterTableData}>
                        {d.doctorName}
                      </td>
                      <td className={masterTableData}>
                        {d.department}
                      </td>
                      <td className={masterTableData}>
                        ₹{d.feeHistory?.[0]?.consultationFee ?? d.consultationFee ?? 0}
                      </td>
                      <td className={masterTableData}>
                        <span className={`px-2  rounded-full text-xs font-semibold ${d.status === 'Active' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
                          {d.status}
                        </span>
                      </td>
                    <td className="p-0.5 border border-slate-400">
                          <div className="flex justify-center gap-3 ">
                          <button 
                            onClick={() => { setFormData({ ...d, feeHistory: normalizeFeeHistory(d.feeHistory) }); setEditingDoctorId(d._id); setShowForm(true); }} 
                            className="rounded-lg p-0.5.5 text-emerald-600 hover:bg-emerald-100 transition-colors"
                          >
                            <Edit size={14} />
                          </button>
                          <button 
                            onClick={() => handleDelete(d)} 
                            className="rounded-lg p-0.5.5 text-rose-600 hover:bg-rose-100 transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="p-0.52 text-center text-slate-400 uppercase text-[10px] tracking-widest font-bold">
                      No matching records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) :  (
          <form onSubmit={handleSubmit} className="max-w-7xl mx-auto space-y-1">
           <div className="bg-white p-1 rounded-xl border border-slate-400 shadow-sm">
            <div className="text-emerald-600 mb-1 border-b  text-[10px] uppercase flex items-center gap-0.5 font-black">
              <User size={14}/> 1. Profile Information
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 border-slate-500 gap-x-1 gap-y-1">
              {/* <InputField label="Doc ID" name="doctorId" value={formData.doctorId} readOnly className="bg-slate-100 font-black text-emerald-600" /> */}
              
              {/* Prefix Field (Disabled) */}
              <InputField label="Prefix" name="prefix" value={formData.prefix} readOnly className="bg-slate-100" />
              
              <InputField label="Name" name="doctorName" value={formData.doctorName} onChange={handleChange} />
              
              {/* Gender Dropdown */}
              <div className="flex flex-col gap-0.5">
                <label className="text-[10px] uppercase text-[#1b2559] font-bold tracking-wider">Gender</label>
                <select name="gender" value={formData.gender} onChange={handleChange} className="border border-slate-400 rounded p-0.5 text-[11px] h-[22px] outline-none">
                  <option value="">SELECT</option>
                  <option value="MALE">MALE</option>
                  <option value="FEMALE">FEMALE</option>
                  <option value="TRANSGENDER">TRANSGENDER</option>
                </select>
              </div>

              <InputField label="DOB" name="dob" value={formData.dob} onChange={handleChange} placeholder="DD/MM/YYYY" />
              <InputField label="Mobile" name="mobile" value={formData.mobile} onChange={handleChange} inputMode="numeric" maxLength={10} />
              <InputField label="Reg No" name="registrationNumber" value={formData.registrationNumber} onChange={handleChange} />
              <InputField label="Email" name="email" value={formData.email} onChange={handleChange} />
              <InputField label="Qualification" name="qualification" value={formData.qualification} onChange={handleChange} />
              
              <div className="flex flex-col gap-0.5">
                <label className="text-[8px] text-emerald-600 uppercase">Dept</label>
                <select name="department" value={formData.department} onChange={handleChange} className="border border-slate-400 rounded p-0.5 text-[11px] h-[22px] outline-none">
                  <option value="">SELECT</option>
                  {departments
                .filter(d => d.status === 'Active' || d.deptName === formData.department)
                .map(d =>
                 <option key={d._id} value={d.deptName}>{d.deptName}</option>)}
                </select>
              </div>
              
              <InputField label="Joining Date" name="joiningDate" value={formData.joiningDate} onChange={handleChange} />
              <InputField label="Termination" name="terminationDate" value={formData.terminationDate} onChange={handleChange} placeholder="DD/MM/YYYY" />
              
              <div className="flex flex-col gap-0.5">
                <label className="text-[8px] text-emerald-600 uppercase">Status</label>
                <select name="status" value={formData.status} onChange={handleChange} className="border border-slate-400 rounded p-0.5 text-[11px] h-[22px] outline-none">
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>

            {/* NEW ADDRESS SECTION HEADER */}
            <div className="text-emerald-600 mt-1 mb-1 border-b pb-1 text-[10px] uppercase flex items-center gap-1 font-black">
              <MapPin size={14}/> 2. Address Details
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-x-1 gap-y-1">
              <InputField label="Vill/Town" name="vill" value={formData.vill} onChange={handleChange} />
              <InputField label="P.O" name="po" value={formData.po} onChange={handleChange} />
              <InputField label="P.S" name="ps" value={formData.ps} onChange={handleChange} />
              <InputField label="District" name="dist" value={formData.dist} onChange={handleChange} />
              <InputField label="State" name="stateName" value={formData.stateName} onChange={handleChange} />
              <InputField label="PIN" name="pin" value={formData.pin} onChange={handleChange} />
            </div>
          </div>

            <div className="bg-white p-0.5 rounded-xl border border-slate-400 shadow-sm">
          <div className="flex items-center justify-between mb-1 border-b pb-1">
            <div className="text-emerald-600 text-[10px] uppercase flex items-center gap-1 font-black">
              <CreditCard size={14} /> 2. Fees & Revenue History
            </div>
            <button
              type="button"
              onClick={() =>
                setFormData((p) => ({
                  ...p,
                  feeHistory: [
                    {
                      ...createFeeHistoryRow(),
                    },
                    ...p.feeHistory,
                  ],
                }))
              }
              className="bg-emerald-500 text-white px-3 py-1 rounded text-[9px] hover:bg-emerald-600"
            >
              + ADD ROW
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border border-slate-500">
              <thead>
                <tr className="bg-slate-100 text-[8px] uppercase text-slate-700 sticky top-0 z-10">
                  <th className="border border-slate-500">S.No</th>
                  <th className="border border-slate-500">Fee (INR)</th>
                  <th className="border border-slate-500">Type</th>
                  <th className="border border-slate-500">Doc Share</th>
                  <th className="border border-slate-500">Hosp Share</th>
                  <th className="border border-slate-500">From Date</th>
                  <th className="border border-slate-500">To Date</th>
                  <th className="border border-slate-500">X</th>
                </tr>
              </thead>
              <tbody>
                {formData.feeHistory.map((row, idx) => (
                  <tr key={row.rowKey} className="text-center">
                    <td className="border p-0.5 border-slate-500 text-slate-400 font-bold">
                      {formData.feeHistory.length - idx}
                    </td>
                    <td className="border border-slate-500">
                      <input
                        type="number"
                        value={row.consultationFee}
                        onChange={(e) => handleFeeChange(row.rowKey, 'consultationFee', e.target.value)}
                        className="w-full outline-none p-0.5 text-center font-black text-emerald-600"
                      />
                    </td>
                    <td className="border border-slate-500">
                        <select
                          value={row.revenueShareType}
                        onChange={(e) => handleFeeChange(row.rowKey, 'revenueShareType', e.target.value)}
                        className="w-full outline-none p-0.5 bg-transparent"
                      >
                        <option value="Percentage">Pct %</option>
                        <option value="Fixed">Fixed</option>
                      </select>
                    </td>
                    <td className="border border-slate-500">
                      <input
                        type="number"
                        value={row.doctorShare}
                        onChange={(e) => handleFeeChange(row.rowKey, 'doctorShare', e.target.value)}
                        className="w-full outline-none p-0.5 text-center"
                      />
                    </td>
                    <td className="border border-slate-500">
                      <input
                        type="number"
                        value={row.centerShare}
                        onChange={(e) => handleFeeChange(row.rowKey, 'centerShare', e.target.value)}
                        className="w-full outline-none p-0.5 text-center bg-slate-50"
                        readOnly={row.revenueShareType === 'Percentage'}
                      />
                    </td>
                    <td className="border border-slate-500">
                      <input
                        type="text"
                        value={row.fromDate}
                        placeholder="DD/MM/YYYY"
                        onChange={(e) => handleFeeChange(row.rowKey, 'fromDate', e.target.value)}
                        className="w-full outline-none p-0.5 text-center"
                      />
                    </td>
                    <td className="border border-slate-500">
                      <input
                        type="text"
                        value={row.toDate}
                        placeholder="DD/MM/YYYY"
                        onChange={(e) => handleFeeChange(row.rowKey, 'toDate', e.target.value)}
                        className="w-full outline-none p-0.5 text-center"
                      />
                    </td>
                    <td className="border border-slate-500">
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((p) => ({
                            ...p,
                            feeHistory: p.feeHistory.length > 1 ? p.feeHistory.filter((r) => r.rowKey !== row.rowKey) : p.feeHistory,
                          }))
                        }
                        className="text-rose-500 hover:bg-rose-50 p-0.5 rounded"
                      >
                        <Trash size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
            <div className="flex justify-end gap-3">
            <button type="submit" disabled={loading} className="px-4 py-1 bg-emerald-600 text-white rounded-xl shadow-lg hover:bg-emerald-700 flex items-center gap-2 uppercase tracking-widest">{loading ? <Loader2 className="animate-spin" size={16}/> : <Save size={16}/>} SAVE</button></div>
          </form>
        )}
      </main>
    </div>
  );
};

const InputField = ({ label, className = "", ...props }) => (
  <div className="flex flex-col">
    <label className={masterInputLabel}>{label}</label>
    <input {...props} className={masterInput} />
  </div>
);

export default DoctorRegistration;
